/** Bao lâu thì bỏ chờ ảnh/CSS và cứ in — tránh treo khi một tài nguyên không tải được. */
const ASSET_TIMEOUT_MS = 5000;

/** Giữ iframe sống thêm một lúc sau khi gọi print, nếu không Safari in ra trang trắng. */
const CLEANUP_DELAY_MS = 1000;

/** CSS của trang hiện tại, để tài liệu in thừa hưởng font và bảng biểu. */
export const collectPageStyles = (): string =>
  Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map((element) => element.outerHTML)
    .join('\n');

const waitForImages = (doc: Document): Promise<void> => {
  const pending = Array.from(doc.images).filter((image) => !image.complete);
  if (pending.length === 0) return Promise.resolve();

  return Promise.all(
    pending.map(
      (image) =>
        new Promise<void>((resolve) => {
          image.addEventListener('load', () => resolve(), { once: true });
          // Ảnh hỏng cũng phải cho qua, không thì không bao giờ in được.
          image.addEventListener('error', () => resolve(), { once: true });
        }),
    ),
  ).then(() => undefined);
};

const withTimeout = (promise: Promise<void>, ms: number): Promise<void> =>
  Promise.race([promise, new Promise<void>((resolve) => setTimeout(resolve, ms))]);

/**
 * In một tài liệu HTML hoàn chỉnh qua iframe ẩn.
 *
 * Thay cho `window.open(...)`: cửa sổ in phụ hiện nguyên nội dung thô phía sau
 * hộp thoại in của trình duyệt, và thường bị chặn popup. Iframe thì người dùng
 * không nhìn thấy gì ngoài hộp thoại in.
 *
 * Ngoài ra còn đợi ảnh tải xong rồi mới in — bản cũ gọi `window.print()` ngay
 * trong `onload` của body nên logo và mã QR có thể chưa kịp hiện trên giấy.
 */
export const printHtmlDocument = async (documentHtml: string): Promise<void> => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('tabindex', '-1');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(iframe);

  const cleanUp = () => {
    setTimeout(() => iframe.remove(), CLEANUP_DELAY_MS);
  };

  try {
    const frameWindow = iframe.contentWindow;
    const frameDocument = frameWindow?.document;
    if (!frameWindow || !frameDocument) throw new Error('Không tạo được khung in');

    frameDocument.open();
    frameDocument.write(documentHtml);
    frameDocument.close();

    await withTimeout(waitForImages(frameDocument), ASSET_TIMEOUT_MS);

    frameWindow.focus();
    frameWindow.print();
  } finally {
    cleanUp();
  }
};

interface PrintOptions {
  title: string;
  /** Thẻ <style> riêng của tài liệu, thêm sau CSS của trang. */
  headHtml?: string;
  /** Kèm CSS của trang hiện tại (mặc định có). */
  inheritStyles?: boolean;
}

/** Dựng tài liệu in từ phần thân, tự ghép CSS của trang. */
export const printHtmlBody = (bodyHtml: string, options: PrintOptions): Promise<void> =>
  printHtmlDocument(`<!doctype html>
<html lang="vi">
  <head>
    <meta charset="utf-8" />
    <title>${options.title}</title>
    ${options.inheritStyles === false ? '' : collectPageStyles()}
    ${options.headHtml ?? ''}
  </head>
  <body>${bodyHtml}</body>
</html>`);
